import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    alpha,
    Avatar,
    Box,
    Button,
    Chip,
    Grid,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { getUserDetails, updateStudentSelfProfile } from "../../redux/userRelated/userHandle";
import { underControl } from "../../redux/userRelated/userSlice";
import Popup from "../../components/Popup";
import { PASSWORD_MIN_HELPER, validatePasswordIfProvided } from "../../utils/passwordValidation";
import {
    StudentPageContainer,
    StudentPageHeader,
    StudentPageLoading,
} from "./studentPortalUi";

function initialsFromName(name) {
    if (!name || typeof name !== "string") return "?";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatDate(value) {
    if (!value) return "—";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
}

function Field({ label, value }) {
    return (
        <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
                {label}
            </Typography>
            <Typography variant="body1" fontWeight={500}>
                {value || "—"}
            </Typography>
        </Grid>
    );
}

const StudentProfile = () => {
    const dispatch = useDispatch();
    const { currentUser, userDetails, loading } = useSelector((state) => state.user);
    const student = userDetails || currentUser;
    const studentId = currentUser?._id || currentUser?.id;

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [saving, setSaving] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [popupMessage, setPopupMessage] = useState("");
    const [popupSeverity, setPopupSeverity] = useState(undefined);

    useEffect(() => {
        if (studentId) {
            dispatch(getUserDetails(studentId, "Student"));
        }
    }, [dispatch, studentId]);

    const sclassName = student?.sclassName?.sclassName || student?.teachSclass?.sclassName || "—";
    const schoolName = student?.school?.schoolName || currentUser?.school?.schoolName || "—";

    const handlePasswordSave = async (event) => {
        event.preventDefault();
        if (!studentId) {
            setPopupMessage("Could not identify your account. Sign out and sign in again.");
            setPopupSeverity("error");
            setShowPopup(true);
            return;
        }

        const trimmed = newPassword.trim();
        const pwdErr = validatePasswordIfProvided(trimmed);
        if (!trimmed) {
            setPopupMessage("Enter a new password.");
            setPopupSeverity("error");
            setShowPopup(true);
            return;
        }
        if (pwdErr) {
            setPopupMessage(pwdErr);
            setPopupSeverity("error");
            setShowPopup(true);
            return;
        }
        if (trimmed !== confirmPassword.trim()) {
            setPopupMessage("New password and confirmation do not match.");
            setPopupSeverity("error");
            setShowPopup(true);
            return;
        }

        setSaving(true);
        dispatch(underControl());
        const outcome = await dispatch(updateStudentSelfProfile({ password: trimmed }, studentId));
        setSaving(false);

        if (outcome?.ok) {
            setNewPassword("");
            setConfirmPassword("");
            setPopupMessage("Password updated successfully.");
            setPopupSeverity("success");
        } else {
            setPopupMessage(outcome?.message || "Could not update password.");
            setPopupSeverity("error");
        }
        setShowPopup(true);
    };

    if (loading && !student?.name) {
        return <StudentPageLoading />;
    }

    return (
        <StudentPageContainer maxWidth="md">
            <Popup
                message={popupMessage}
                setShowPopup={setShowPopup}
                showPopup={showPopup}
                severity={popupSeverity}
            />

            <StudentPageHeader eyebrow="Account" title="My profile" subtitle="Your registration details on file at school." />

            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2.5, sm: 3 },
                    mb: 2,
                    borderRadius: 3,
                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.12)}`,
                }}
            >
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5} alignItems="center">
                    <Avatar sx={{ width: 88, height: 88, fontSize: "1.5rem", fontWeight: 800 }}>
                        {initialsFromName(student?.name)}
                    </Avatar>
                    <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
                        <Typography variant="h5" fontWeight={800}>
                            {student?.name || "Student"}
                        </Typography>
                        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                            Roll no. {student?.rollNum ?? "—"} · {sclassName}
                        </Typography>
                        <Chip label={schoolName} size="small" sx={{ mt: 1 }} />
                    </Box>
                </Stack>
            </Paper>

            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2.5, sm: 3 },
                    mb: 2,
                    borderRadius: 3,
                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.12)}`,
                }}
            >
                <Typography variant="h6" fontWeight={700} gutterBottom>
                    Personal information
                </Typography>
                <Grid container spacing={2.5}>
                    <Field label="Gender" value={student?.gender} />
                    <Field label="Date of birth" value={formatDate(student?.dateOfBirth)} />
                    <Field label="Guardian" value={student?.guardianName} />
                    <Field label="Guardian phone" value={student?.guardianPhone || student?.nextOfKinPhone} />
                    <Field label="Guardian email" value={student?.guardianEmail} />
                </Grid>
            </Paper>

            <Paper
                component="form"
                onSubmit={handlePasswordSave}
                elevation={0}
                sx={{
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: 3,
                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.12)}`,
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <LockOutlinedIcon color="primary" fontSize="small" />
                    <Typography variant="h6" fontWeight={700}>
                        Change password
                    </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Use a private password only you know. {PASSWORD_MIN_HELPER}.
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            type="password"
                            label="New password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            autoComplete="new-password"
                            inputProps={{ minLength: 8 }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            type="password"
                            label="Confirm new password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            autoComplete="new-password"
                        />
                    </Grid>
                </Grid>
                <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveOutlinedIcon />}
                    disabled={saving}
                    sx={{ mt: 2.5, borderRadius: 2 }}
                >
                    {saving ? "Saving…" : "Update password"}
                </Button>
            </Paper>
        </StudentPageContainer>
    );
};

export default StudentProfile;
